/**
 * 下载助手工具类
 * 提供安全可靠的文件下载功能
 */
import { DownloadInfo } from '@/pages/Download/hooks/useDownloadInfo';

import { getSystemInfo } from './systemDetector';
import {
  showDownloadErrorToast,
  showDownloadStartToast,
  showDownloadUnavailableToast,
  showOpeningDownloadPageToast,
  showPopupBlockedToast,
} from './toastHelper';

/**
 * 触发文件下载
 * @param url 下载链接
 * @param filename 文件名（可选）
 * @returns Promise<boolean> 下载是否成功触发
 */
export function triggerDownload(url: string, filename?: string): Promise<boolean> {
  return new Promise((resolve) => {
    try {
      if (!url) {
        console.error('下载链接为空');
        resolve(false);
        return;
      }

      // 创建隐藏的下载链接
      const link = document.createElement('a');
      link.href = url;

      // 设置文件名
      if (filename) {
        link.download = filename;
      } else {
        link.download = '';
      }

      // 设置样式确保隐藏
      link.style.display = 'none';
      link.style.visibility = 'hidden';

      // 添加到DOM
      document.body.appendChild(link);

      // 触发点击
      link.click();

      // 清理DOM
      setTimeout(() => {
        document.body.removeChild(link);
        resolve(true);
      }, 100);
    } catch (error) {
      console.error('下载失败:', error);
      resolve(false);
    }
  });
}

/**
 * 根据下载信息获取最佳下载链接
 * @param downloadInfo 下载信息
 * @returns 最佳下载链接
 */
export function getBestDownloadUrl(downloadInfo: DownloadInfo): string {
  // 优先使用 DMG 链接，然后是普通下载链接
  return downloadInfo.dmgUrl || downloadInfo.url || '';
}

/**
 * 从下载信息中提取文件名
 * @param downloadInfo 下载信息
 * @returns 文件名
 */
export function extractFilename(downloadInfo: DownloadInfo): string {
  // 优先使用 filename 字段
  if (downloadInfo.filename) {
    return downloadInfo.filename;
  }

  // 从URL中提取文件名
  const url = getBestDownloadUrl(downloadInfo);
  if (url) {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const filename = pathname.split('/').pop();
      return filename || '';
    } catch (error) {
      console.warn('无法从URL提取文件名:', error);
    }
  }

  return '';
}

/**
 * 检查下载链接是否有效
 * @param url 下载链接
 * @returns Promise<boolean> 链接是否有效
 */
export async function checkDownloadUrl(url: string): Promise<boolean> {
  try {
    if (!url) return false;

    // 使用 HEAD 请求检查链接有效性
    const response = await fetch(url, {
      method: 'HEAD',
      mode: 'no-cors', // 避免 CORS 问题
    });

    return response.ok || response.type === 'opaque';
  } catch (error) {
    console.warn('检查下载链接失败:', error);
    // 即使检查失败，也假设链接有效，让浏览器处理
    return true;
  }
}

/**
 * 安全下载文件
 * @param downloadInfo 下载信息
 * @returns Promise<boolean> 下载是否成功
 */
export async function safeDownload(downloadInfo: DownloadInfo): Promise<boolean> {
  try {
    const url = getBestDownloadUrl(downloadInfo);
    const filename = extractFilename(downloadInfo);

    if (!url) {
      console.error('没有可用的下载链接');
      showDownloadUnavailableToast();
      return false;
    }

    // 确定芯片类型显示文本
    let chipType = 'Unknown';

    if (downloadInfo.architecture === 'arm64') {
      chipType = 'Apple Chip';
    } else if (downloadInfo.architecture === 'x64') {
      chipType = 'Intel Chip';
    } else if (downloadInfo.platform === 'win32') {
      chipType = 'Windows';
    }

    // 显示开始下载的 toast 提示
    showDownloadStartToast(chipType, downloadInfo.version);

    // 检查链接有效性（可选，不阻塞下载）
    const isValid = await checkDownloadUrl(url);
    if (!isValid) {
      console.warn('下载链接可能无效，但仍尝试下载');
    }

    // 触发下载
    return await triggerDownload(url, filename);
  } catch (error) {
    console.error('下载过程中发生错误:', error);
    showDownloadErrorToast();
    return false;
  }
}

/**
 * 在新窗口中打开下载链接（备用方案）
 * @param url 下载链接
 */
export function openDownloadInNewWindow(url: string): void {
  try {
    if (!url) {
      console.error('下载链接为空');
      showDownloadUnavailableToast();
      return;
    }

    // 显示正在打开下载页面的提示
    showOpeningDownloadPageToast();

    // 在新窗口中打开下载链接
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');

    // 如果弹窗被阻止，提示用户
    if (!newWindow) {
      showPopupBlockedToast();
    }
  } catch (error) {
    console.error('在新窗口打开下载链接失败:', error);
    showDownloadErrorToast('Failed to open download page');
  }
}
